import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  AccountBalance,
  Add,
  Delete,
  Edit,
  Email,
  FileDownload,
  FilterAlt,
  Person,
  Phone,
  PictureAsPdf,
  Search
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, isValid, parseISO } from 'date-fns';
import { exportLedgerToExcel, exportLedgerToPdf } from '../../utils/ledgerExport';

type AccountType =
  | 'Customer'
  | 'Vendor'
  | 'Bank'
  | 'Cash'
  | 'Employee'
  | 'Partner'
  | 'Loan'
  | 'Expense'
  | 'Other';
type AccountStatus = 'active' | 'inactive';

type EntryType = 'credit' | 'debit';
type EntryCategory =
  | 'Sale'
  | 'Service'
  | 'Purchase'
  | 'Expense'
  | 'Salary'
  | 'Rent'
  | 'Utility'
  | 'Transfer'
  | 'Loan'
  | 'Refund'
  | 'Adjustment'
  | 'Other';
type PaymentMode = 'Cash' | 'Bank Transfer' | 'UPI' | 'Card' | 'Cheque' | 'Wallet' | 'Other';

interface Account {
  id: string;
  accountName: string;
  accountType: AccountType;
  contactPerson: string;
  phone: string;
  email: string;
  openingBalance: number;
  currentBalance: number;
  status: AccountStatus;
  notes: string;
  createdAt: string;
  updatedAt: string;
}

interface LedgerEntry {
  id: string;
  accountId: string;
  entryType: EntryType;
  amount: number;
  entryDate: string;
  category: EntryCategory;
  paymentMode: PaymentMode;
  referenceNo: string;
  notes: string;
  createdAt: string;
}

type AccountForm = Omit<Account, 'id' | 'createdAt' | 'updatedAt' | 'currentBalance'>;
type EntryForm = Omit<LedgerEntry, 'id' | 'createdAt'>;

const ACCOUNT_STORAGE_KEY = 'mfs_accounts_v2';
const ENTRY_STORAGE_KEY = 'mfs_account_ledger_v1';
const LEGACY_ACCOUNT_STORAGE_KEY = 'mfs_accounts';

const ACCOUNT_TYPES: AccountType[] = [
  'Customer',
  'Vendor',
  'Bank',
  'Cash',
  'Employee',
  'Partner',
  'Loan',
  'Expense',
  'Other',
];
const ENTRY_CATEGORIES: EntryCategory[] = [
  'Sale',
  'Service',
  'Purchase',
  'Expense',
  'Salary',
  'Rent',
  'Utility',
  'Transfer',
  'Loan',
  'Refund',
  'Adjustment',
  'Other',
];
const PAYMENT_MODES: PaymentMode[] = [
  'Cash',
  'Bank Transfer',
  'UPI',
  'Card',
  'Cheque',
  'Wallet',
  'Other',
];

const toStorageDate = (value: Date): string => format(value, 'yyyy-MM-dd');
const round2 = (value: number): number => Math.round(value * 100) / 100;
const formatMoney = (amount: number): string =>
  new Intl.NumberFormat('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount);

const safeParse = <T,>(raw: string | null): T[] => {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as T[]) : [];
  } catch {
    return [];
  }
};

const normalizeDate = (value: string | null | undefined): string => {
  if (!value) return toStorageDate(new Date());
  const parsed = parseISO(value);
  return isValid(parsed) ? toStorageDate(parsed) : toStorageDate(new Date());
};

const getDefaultAccountForm = (): AccountForm => ({
  accountName: '',
  accountType: 'Customer',
  contactPerson: '',
  phone: '',
  email: '',
  openingBalance: 0,
  status: 'active',
  notes: '',
});

const getDefaultEntryForm = (): EntryForm => ({
  accountId: '',
  entryType: 'credit',
  amount: 0,
  entryDate: toStorageDate(new Date()),
  category: 'Sale',
  paymentMode: 'Cash',
  referenceNo: '',
  notes: '',
});

const AccountsPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [accounts, setAccounts] = useState<Account[]>([]);
  const [entries, setEntries] = useState<LedgerEntry[]>([]);

  const [accountDialogOpen, setAccountDialogOpen] = useState(false);
  const [entryDialogOpen, setEntryDialogOpen] = useState(false);
  const [editAccountId, setEditAccountId] = useState<string | null>(null);
  const [accountForm, setAccountForm] = useState<AccountForm>(getDefaultAccountForm);
  const [entryForm, setEntryForm] = useState<EntryForm>(getDefaultEntryForm);

  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<'all' | AccountType>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | AccountStatus>('all');

  const [ledgerAccountFilter, setLedgerAccountFilter] = useState<'all' | string>('all');
  const [ledgerTypeFilter, setLedgerTypeFilter] = useState<'all' | EntryType>('all');
  const [ledgerFromDate, setLedgerFromDate] = useState<string | null>(null);
  const [ledgerToDate, setLedgerToDate] = useState<string | null>(null);

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const showSnackbar = (
    message: string,
    severity: 'success' | 'error' | 'info' | 'warning'
  ) => {
    setSnackbar({ open: true, message, severity });
  };

  useEffect(() => {
    const nextAccounts = safeParse<Account>(localStorage.getItem(ACCOUNT_STORAGE_KEY));
    if (nextAccounts.length > 0) {
      setAccounts(
        nextAccounts.map((account) => ({
          ...account,
          openingBalance: Number(account.openingBalance) || 0,
          currentBalance: Number(account.currentBalance) || 0,
          status: account.status === 'inactive' ? 'inactive' : 'active',
          accountType: ACCOUNT_TYPES.includes(account.accountType) ? account.accountType : 'Other',
        }))
      );
    } else {
      const legacyAccounts = safeParse<Account>(localStorage.getItem(LEGACY_ACCOUNT_STORAGE_KEY));
      if (legacyAccounts.length > 0) {
        setAccounts(
          legacyAccounts.map((account) => ({
            ...account,
            openingBalance: Number(account.openingBalance) || 0,
            currentBalance: Number(account.currentBalance) || 0,
            status: account.status === 'inactive' ? 'inactive' : 'active',
            accountType: ACCOUNT_TYPES.includes(account.accountType) ? account.accountType : 'Other',
          }))
        );
        showSnackbar('Accounts migrated to new storage.', 'info');
      }
    }

    const nextEntries = safeParse<LedgerEntry>(localStorage.getItem(ENTRY_STORAGE_KEY));
    if (nextEntries.length > 0) {
      setEntries(
        nextEntries.map((entry) => ({
          ...entry,
          amount: Number(entry.amount) || 0,
          entryType: entry.entryType === 'debit' ? 'debit' : 'credit',
          entryDate: normalizeDate(entry.entryDate),
          category: ENTRY_CATEGORIES.includes(entry.category) ? entry.category : 'Other',
          paymentMode: PAYMENT_MODES.includes(entry.paymentMode) ? entry.paymentMode : 'Other',
        }))
      );
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(ACCOUNT_STORAGE_KEY, JSON.stringify(accounts));
  }, [accounts]);

  useEffect(() => {
    localStorage.setItem(ENTRY_STORAGE_KEY, JSON.stringify(entries));
  }, [entries]);

  const hasAccountFilters = Boolean(searchTerm || typeFilter !== 'all' || statusFilter !== 'all');
  const hasLedgerFilters = Boolean(
    ledgerAccountFilter !== 'all' || ledgerTypeFilter !== 'all' || ledgerFromDate || ledgerToDate
  );

  const accountMap = useMemo(() => {
    const map = new Map<string, Account>();
    accounts.forEach((account) => map.set(account.id, account));
    return map;
  }, [accounts]);

  const filteredAccounts = useMemo(() => {
    return accounts
      .filter((account) => {
        const query = searchTerm.trim().toLowerCase();
        const matchesSearch = query
          ? account.accountName.toLowerCase().includes(query) ||
          account.contactPerson.toLowerCase().includes(query) ||
          account.phone.toLowerCase().includes(query) ||
          account.email.toLowerCase().includes(query)
          : true;
        const matchesType = typeFilter === 'all' ? true : account.accountType === typeFilter;
        const matchesStatus = statusFilter === 'all' ? true : account.status === statusFilter;
        return matchesSearch && matchesType && matchesStatus;
      })
      .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
  }, [accounts, searchTerm, typeFilter, statusFilter]);

  const filteredEntries = useMemo(() => {
    return entries
      .filter((entry) => {
        if (!accountMap.has(entry.accountId)) return false;
        const matchesAccount = ledgerAccountFilter === 'all' ? true : entry.accountId === ledgerAccountFilter;
        const matchesType = ledgerTypeFilter === 'all' ? true : entry.entryType === ledgerTypeFilter;
        const matchesFrom = ledgerFromDate ? entry.entryDate >= ledgerFromDate : true;
        const matchesTo = ledgerToDate ? entry.entryDate <= ledgerToDate : true;
        return matchesAccount && matchesType && matchesFrom && matchesTo;
      })
      .sort((a, b) => {
        if (a.entryDate !== b.entryDate) return b.entryDate.localeCompare(a.entryDate);
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      });
  }, [entries, accountMap, ledgerAccountFilter, ledgerTypeFilter, ledgerFromDate, ledgerToDate]);

  const accountStats = useMemo(() => {
    const totalBalance = filteredAccounts.reduce((sum, account) => sum + account.currentBalance, 0);
    const activeCount = filteredAccounts.filter((account) => account.status === 'active').length;
    const receivable = filteredAccounts.reduce(
      (sum, account) => (account.currentBalance > 0 ? sum + account.currentBalance : sum),
      0
    );
    const payable = filteredAccounts.reduce(
      (sum, account) => (account.currentBalance < 0 ? sum + Math.abs(account.currentBalance) : sum),
      0
    );
    return { totalBalance, activeCount, receivable, payable };
  }, [filteredAccounts]);

  const ledgerTotals = useMemo(() => {
    const credit = filteredEntries.reduce(
      (sum, entry) => (entry.entryType === 'credit' ? sum + entry.amount : sum),
      0
    );
    const debit = filteredEntries.reduce(
      (sum, entry) => (entry.entryType === 'debit' ? sum + entry.amount : sum),
      0
    );
    return { credit, debit, net: credit - debit };
  }, [filteredEntries]);

  const dailyLedger = useMemo(() => {
    const grouped: Record<
      string,
      {
        date: string;
        credit: number;
        debit: number;
        entries: LedgerEntry[];
      }
    > = {};

    filteredEntries.forEach((entry) => {
      if (!grouped[entry.entryDate]) {
        grouped[entry.entryDate] = { date: entry.entryDate, credit: 0, debit: 0, entries: [] };
      }
      grouped[entry.entryDate].entries.push(entry);
      if (entry.entryType === 'credit') {
        grouped[entry.entryDate].credit += entry.amount;
      } else {
        grouped[entry.entryDate].debit += entry.amount;
      }
    });

    return Object.values(grouped).sort((a, b) => b.date.localeCompare(a.date));
  }, [filteredEntries]);

  const handleAccountTextChange =
    (field: keyof Omit<AccountForm, 'openingBalance' | 'accountType' | 'status'>) =>
      (event: React.ChangeEvent<HTMLInputElement>) => {
        setAccountForm((prev) => ({
          ...prev,
          [field]: event.target.value,
        }));
      };

  const handleOpeningBalanceChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = Number(event.target.value);
    setAccountForm((prev) => ({
      ...prev,
      openingBalance: Number.isFinite(value) ? value : 0,
    }));
  };

  const resetAccountForm = () => {
    setAccountForm(getDefaultAccountForm());
    setEditAccountId(null);
    setAccountDialogOpen(false);
  };

  const resetAccountFilters = () => {
    setSearchTerm('');
    setTypeFilter('all');
    setStatusFilter('all');
  };

  const openEntryDialog = (accountId?: string, entryType?: EntryType) => {
    if (accounts.length === 0) {
      showSnackbar('Please add an account first.', 'warning');
      return;
    }
    setEntryForm({
      ...getDefaultEntryForm(),
      accountId: accountId || '',
      entryType: entryType || 'credit',
    });
    setEntryDialogOpen(true);
  };

  const resetEntryForm = () => {
    setEntryForm(getDefaultEntryForm());
    setEntryDialogOpen(false);
  };

  const resetLedgerFilters = () => {
    setLedgerAccountFilter('all');
    setLedgerTypeFilter('all');
    setLedgerFromDate(null);
    setLedgerToDate(null);
  };

  const handleSaveAccount = () => {
    if (!accountForm.accountName.trim()) {
      showSnackbar('Account name is required.', 'error');
      return;
    }

    const now = new Date().toISOString();
    const payload: AccountForm = {
      ...accountForm,
      accountName: accountForm.accountName.trim(),
      contactPerson: accountForm.contactPerson.trim(),
      phone: accountForm.phone.trim(),
      email: accountForm.email.trim(),
      notes: accountForm.notes.trim(),
      openingBalance: Number.isFinite(accountForm.openingBalance) ? round2(accountForm.openingBalance) : 0,
    };

    if (editAccountId) {
      setAccounts((prev) =>
        prev.map((account) => {
          if (account.id !== editAccountId) return account;
          const openingDelta = payload.openingBalance - account.openingBalance;
          return {
            ...account,
            ...payload,
            currentBalance: round2(account.currentBalance + openingDelta),
            updatedAt: now,
          };
        })
      );
      showSnackbar('Account updated.', 'success');
    } else {
      const id = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      const newAccount: Account = {
        id,
        ...payload,
        currentBalance: payload.openingBalance,
        createdAt: now,
        updatedAt: now,
      };
      setAccounts((prev) => [newAccount, ...prev]);
      showSnackbar('Account created.', 'success');
    }

    resetAccountForm();
  };

  const handleEditAccount = (account: Account) => {
    setEditAccountId(account.id);
    setAccountForm({
      accountName: account.accountName,
      accountType: account.accountType,
      contactPerson: account.contactPerson,
      phone: account.phone,
      email: account.email,
      openingBalance: account.openingBalance,
      status: account.status,
      notes: account.notes,
    });
    setAccountDialogOpen(true);
  };

  const handleDeleteAccount = (accountId: string) => {
    const linkedEntries = entries.filter((entry) => entry.accountId === accountId).length;
    const promptMessage =
      linkedEntries > 0
        ? `Delete this account and ${linkedEntries} linked ledger entries?`
        : 'Delete this account?';
    if (!window.confirm(promptMessage)) return;

    setAccounts((prev) => prev.filter((account) => account.id !== accountId));
    if (linkedEntries > 0) {
      setEntries((prev) => prev.filter((entry) => entry.accountId !== accountId));
    }
    showSnackbar('Account deleted.', 'info');
  };

  const toggleAccountStatus = (id: string) => {
    setAccounts((prev) =>
      prev.map((account) =>
        account.id === id
          ? {
            ...account,
            status: account.status === 'active' ? 'inactive' : 'active',
            updatedAt: new Date().toISOString(),
          }
          : account
      )
    );
    showSnackbar('Account status updated.', 'success');
  };

  const handleEntryTextChange =
    (field: keyof Omit<EntryForm, 'amount' | 'entryType' | 'entryDate' | 'accountId' | 'category' | 'paymentMode'>) =>
      (event: React.ChangeEvent<HTMLInputElement>) => {
        setEntryForm((prev) => ({
          ...prev,
          [field]: event.target.value,
        }));
      };

  const handleEntryAmountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = Number(event.target.value);
    setEntryForm((prev) => ({
      ...prev,
      amount: Number.isFinite(value) ? value : 0,
    }));
  };

  const handleSaveEntry = () => {
    if (!entryForm.accountId) {
      showSnackbar('Please select an account.', 'error');
      return;
    }
    if (!accountMap.has(entryForm.accountId)) {
      showSnackbar('Selected account is not available.', 'error');
      return;
    }
    if (!(entryForm.amount > 0)) {
      showSnackbar('Amount must be greater than zero.', 'error');
      return;
    }

    const normalizedDate = normalizeDate(entryForm.entryDate);
    const now = new Date().toISOString();
    const amount = round2(entryForm.amount);

    const newEntry: LedgerEntry = {
      id: `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      accountId: entryForm.accountId,
      entryType: entryForm.entryType,
      amount,
      entryDate: normalizedDate,
      category: entryForm.category,
      paymentMode: entryForm.paymentMode,
      referenceNo: entryForm.referenceNo.trim(),
      notes: entryForm.notes.trim(),
      createdAt: now,
    };

    const delta = newEntry.entryType === 'credit' ? amount : -amount;
    setEntries((prev) => [newEntry, ...prev]);
    setAccounts((prev) =>
      prev.map((account) =>
        account.id === newEntry.accountId
          ? { ...account, currentBalance: round2(account.currentBalance + delta), updatedAt: now }
          : account
      )
    );

    showSnackbar(`${newEntry.entryType === 'credit' ? 'Credit' : 'Debit'} entry saved.`, 'success');
    resetEntryForm();
  };

  const handleDeleteEntry = (entryId: string) => {
    const target = entries.find((entry) => entry.id === entryId);
    if (!target) return;
    if (!window.confirm('Delete this ledger entry?')) return;

    const reverseDelta = target.entryType === 'credit' ? -target.amount : target.amount;
    setEntries((prev) => prev.filter((entry) => entry.id !== entryId));
    setAccounts((prev) =>
      prev.map((account) =>
        account.id === target.accountId
          ? { ...account, currentBalance: round2(account.currentBalance + reverseDelta), updatedAt: new Date().toISOString() }
          : account
      )
    );
    showSnackbar('Ledger entry deleted.', 'info');
  };

  const handleExportFullExcel = () => {
    if (accounts.length === 0 && entries.length === 0) {
      showSnackbar('No account or ledger data available to export.', 'warning');
      return;
    }
    try {
      exportLedgerToExcel(accounts, entries, accountMap, 'Full_Ledger');
      showSnackbar('Full Ledger exported to Excel (.xlsx) successfully.', 'success');
    } catch (err) {
      showSnackbar('Failed to export Full Ledger to Excel.', 'error');
    }
  };

  const handleExportFullPdf = () => {
    if (accounts.length === 0 && entries.length === 0) {
      showSnackbar('No account or ledger data available to export.', 'warning');
      return;
    }
    try {
      exportLedgerToPdf(accounts, entries, accountMap, 'FULL LEDGER STATEMENT', 'Full_Ledger');
      showSnackbar('Full Ledger exported to PDF (.pdf) successfully.', 'success');
    } catch (err) {
      showSnackbar('Failed to export Full Ledger to PDF.', 'error');
    }
  };

  const handleExportFilteredExcel = () => {
    if (filteredAccounts.length === 0 && filteredEntries.length === 0) {
      showSnackbar('No matching ledger data available for current filters.', 'warning');
      return;
    }
    try {
      exportLedgerToExcel(filteredAccounts, filteredEntries, accountMap, 'Filtered_Ledger');
      showSnackbar('Filtered Ledger exported to Excel (.xlsx) successfully.', 'success');
    } catch (err) {
      showSnackbar('Failed to export Filtered Ledger to Excel.', 'error');
    }
  };

  const handleExportFilteredPdf = () => {
    if (filteredAccounts.length === 0 && filteredEntries.length === 0) {
      showSnackbar('No matching ledger data available for current filters.', 'warning');
      return;
    }
    try {
      exportLedgerToPdf(filteredAccounts, filteredEntries, accountMap, 'FILTERED LEDGER STATEMENT', 'Filtered_Ledger');
      showSnackbar('Filtered Ledger exported to PDF (.pdf) successfully.', 'success');
    } catch (err) {
      showSnackbar('Failed to export Filtered Ledger to PDF.', 'error');
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Paper
          elevation={0}
          sx={{
            mb: 3,
            p: { xs: 2.5, md: 3.5 },
            borderRadius: 3,
            color: 'common.white',
            background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 65%, ${theme.palette.secondary.main} 100%)`,
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
            <Box>
              <Typography variant={isMobile ? 'h5' : 'h4'} sx={{ fontWeight: 700 }} gutterBottom>
                Accounts and Ledger
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.92 }}>
                Track accounts with date-wise credit and debit entries that are saved locally.
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                startIcon={<FileDownload />}
                onClick={handleExportFullExcel}
                sx={{
                  bgcolor: 'common.white',
                  color: 'primary.main',
                  fontWeight: 700,
                  '&:hover': { bgcolor: alpha('#ffffff', 0.9) }
                }}
              >
                Download Excel
              </Button>
              <Button
                variant="contained"
                startIcon={<PictureAsPdf />}
                onClick={handleExportFullPdf}
                sx={{
                  bgcolor: alpha('#ffffff', 0.2),
                  color: 'common.white',
                  fontWeight: 700,
                  border: '1px solid rgba(255, 255, 255, 0.4)',
                  '&:hover': { bgcolor: alpha('#ffffff', 0.3) }
                }}
              >
                Download PDF
              </Button>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2 }}>
            <Chip label={`${filteredAccounts.length} Accounts`} sx={{ bgcolor: alpha('#ffffff', 0.22), color: 'common.white' }} />
            <Chip label={`${accountStats.activeCount} Active`} sx={{ bgcolor: alpha('#ffffff', 0.22), color: 'common.white' }} />
            <Chip label={`Rs ${formatMoney(accountStats.totalBalance)} Net`} sx={{ bgcolor: alpha('#ffffff', 0.22), color: 'common.white' }} />
            <Chip label={`${entries.length} Ledger Entries`} sx={{ bgcolor: alpha('#ffffff', 0.22), color: 'common.white' }} />
          </Box>
        </Paper>

        <Box sx={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)', gap: 2, mb: 3 }}>
          <Paper elevation={0} sx={{ p: 2, borderRadius: 2.5, border: `1px solid ${alpha(theme.palette.divider, 0.8)}` }}>
            <Typography variant="caption" color="text.secondary">Receivable</Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5, color: 'info.main' }}>Rs {formatMoney(accountStats.receivable)}</Typography>
          </Paper>
          <Paper elevation={0} sx={{ p: 2, borderRadius: 2.5, border: `1px solid ${alpha(theme.palette.divider, 0.8)}` }}>
            <Typography variant="caption" color="text.secondary">Payable</Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5, color: 'error.main' }}>Rs {formatMoney(accountStats.payable)}</Typography>
          </Paper>
          <Paper elevation={0} sx={{ p: 2, borderRadius: 2.5, border: `1px solid ${alpha(theme.palette.success.main, 0.26)}`, bgcolor: alpha(theme.palette.success.main, 0.08) }}>
            <Typography variant="caption" color="text.secondary">Filtered Credit</Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5, color: 'success.main' }}>Rs {formatMoney(ledgerTotals.credit)}</Typography>
          </Paper>
          <Paper elevation={0} sx={{ p: 2, borderRadius: 2.5, border: `1px solid ${alpha(theme.palette.warning.main, 0.26)}`, bgcolor: alpha(theme.palette.warning.main, 0.1) }}>
            <Typography variant="caption" color="text.secondary">Filtered Debit</Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5, color: 'warning.dark' }}>Rs {formatMoney(ledgerTotals.debit)}</Typography>
          </Paper>
        </Box>

        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            mb: 3,
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
            boxShadow: `0 10px 28px ${alpha(theme.palette.common.black, 0.06)}`,
          }}
        >
          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
            <FilterAlt sx={{ mr: 1 }} /> Account Filters
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr 1fr auto auto auto', gap: 1.5, alignItems: 'center' }}>
            <TextField
              size="small"
              fullWidth
              label="Search account, contact, phone, email"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <FormControl size="small" fullWidth>
              <InputLabel>Type</InputLabel>
              <Select label="Type" value={typeFilter} onChange={(event) => setTypeFilter(event.target.value as 'all' | AccountType)}>
                <MenuItem value="all">All Types</MenuItem>
                {ACCOUNT_TYPES.map((type) => (
                  <MenuItem key={type} value={type}>{type}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth>
              <InputLabel>Status</InputLabel>
              <Select label="Status" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value as 'all' | AccountStatus)}>
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
            <Button variant="outlined" color="inherit" onClick={resetAccountFilters} disabled={!hasAccountFilters} sx={{ height: 40 }} fullWidth={isMobile}>
              Reset
            </Button>
            <Button variant="outlined" color="success" startIcon={<Add />} onClick={() => openEntryDialog()} sx={{ height: 40 }} fullWidth={isMobile}>
              Credit / Debit
            </Button>
            <Button variant="contained" startIcon={<Add />} onClick={() => setAccountDialogOpen(true)} sx={{ height: 40 }} fullWidth={isMobile}>
              Add Account
            </Button>
          </Box>
        </Paper>

        {filteredAccounts.length > 0 ? (
          <Box sx={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(330px, 1fr))', gap: 2.5, mb: 3 }}>
            {filteredAccounts.map((account) => (
              <Paper
                key={account.id}
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 3,
                  border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
                  boxShadow: `0 8px 22px ${alpha(theme.palette.common.black, 0.05)}`,
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, minWidth: 0 }}>
                    <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
                      {account.accountName.charAt(0).toUpperCase()}
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }} noWrap>{account.accountName}</Typography>
                      <Typography variant="body2" color="text.secondary">{account.accountType}</Typography>
                    </Box>
                  </Box>
                  <Box>
                    <Tooltip title="Edit">
                      <IconButton size="small" color="primary" onClick={() => handleEditAccount(account)}>
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" color="error" onClick={() => handleDeleteAccount(account.id)}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>

                <Box sx={{ mt: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Chip size="small" label={account.status === 'active' ? 'Active' : 'Inactive'} color={account.status === 'active' ? 'success' : 'default'} />
                  <Chip size="small" icon={<AccountBalance fontSize="small" />} label={`Rs ${formatMoney(account.currentBalance)}`} color={account.currentBalance >= 0 ? 'info' : 'error'} variant="outlined" />
                </Box>

                <Box sx={{ mt: 1.5, display: 'grid', gap: 0.8 }}>
                  {account.contactPerson && (
                    <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
                      <Person sx={{ mr: 0.8 }} fontSize="small" /> {account.contactPerson}
                    </Typography>
                  )}
                  {account.phone && (
                    <Typography component="a" href={`tel:${account.phone}`} variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none', '&:hover': { color: 'primary.main' } }}>
                      <Phone sx={{ mr: 0.8 }} fontSize="small" /> {account.phone}
                    </Typography>
                  )}
                  {account.email && (
                    <Typography component="a" href={`mailto:${account.email}`} variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none', '&:hover': { color: 'primary.main' } }}>
                      <Email sx={{ mr: 0.8 }} fontSize="small" /> {account.email}
                    </Typography>
                  )}
                </Box>

                {account.notes && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
                    {account.notes}
                  </Typography>
                )}

                <Box sx={{ mt: 1.8, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button size="small" variant="text" onClick={() => toggleAccountStatus(account.id)}>
                    Mark as {account.status === 'active' ? 'Inactive' : 'Active'}
                  </Button>
                  <Button size="small" variant="outlined" color="success" onClick={() => openEntryDialog(account.id, 'credit')}>
                    Credit
                  </Button>
                  <Button size="small" variant="outlined" color="error" onClick={() => openEntryDialog(account.id, 'debit')}>
                    Debit
                  </Button>
                </Box>
              </Paper>
            ))}
          </Box>
        ) : (
          <Paper
            elevation={0}
            sx={{
              p: 5,
              textAlign: 'center',
              borderRadius: 3,
              border: `1px dashed ${alpha(theme.palette.divider, 0.9)}`,
              mb: 3,
            }}
          >
            <AccountBalance sx={{ fontSize: 46, color: 'text.disabled', mb: 1 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No accounts found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Add a new account or adjust your filters.
            </Typography>
            <Button variant="contained" sx={{ mt: 2 }} startIcon={<Add />} onClick={() => setAccountDialogOpen(true)}>
              Add Account
            </Button>
          </Paper>
        )}

        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            mb: 2,
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
            boxShadow: `0 10px 28px ${alpha(theme.palette.common.black, 0.06)}`,
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
              <FilterAlt sx={{ mr: 1 }} /> Date Wise Credit and Debit
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button size="small" variant="outlined" color="success" startIcon={<FileDownload />} onClick={handleExportFilteredExcel} disabled={filteredEntries.length === 0}>
                Export View Excel
              </Button>
              <Button size="small" variant="outlined" color="error" startIcon={<PictureAsPdf />} onClick={handleExportFilteredPdf} disabled={filteredEntries.length === 0}>
                Export View PDF
              </Button>
            </Box>
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1.4fr 1fr 1fr 1fr auto auto', gap: 1.2 }}>
            <FormControl size="small" fullWidth>
              <InputLabel>Account</InputLabel>
              <Select label="Account" value={ledgerAccountFilter} onChange={(event) => setLedgerAccountFilter(event.target.value as 'all' | string)}>
                <MenuItem value="all">All Accounts</MenuItem>
                {accounts.map((account) => (
                  <MenuItem key={account.id} value={account.id}>{account.accountName}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" fullWidth>
              <InputLabel>Type</InputLabel>
              <Select label="Type" value={ledgerTypeFilter} onChange={(event) => setLedgerTypeFilter(event.target.value as 'all' | EntryType)}>
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="credit">Credit</MenuItem>
                <MenuItem value="debit">Debit</MenuItem>
              </Select>
            </FormControl>

            <DatePicker
              label="From"
              value={ledgerFromDate ? parseISO(ledgerFromDate) : null}
              onChange={(value) => setLedgerFromDate(value ? toStorageDate(value) : null)}
              format="dd-MM-yyyy"
              slotProps={{ textField: { size: 'small', fullWidth: true } }}
            />

            <DatePicker
              label="To"
              value={ledgerToDate ? parseISO(ledgerToDate) : null}
              onChange={(value) => setLedgerToDate(value ? toStorageDate(value) : null)}
              format="dd-MM-yyyy"
              slotProps={{ textField: { size: 'small', fullWidth: true } }}
            />

            <Button variant="outlined" color="inherit" onClick={resetLedgerFilters} disabled={!hasLedgerFilters} sx={{ height: 40 }} fullWidth={isMobile}>
              Reset
            </Button>

            <Button variant="contained" color="success" startIcon={<Add />} onClick={() => openEntryDialog()} sx={{ height: 40 }} fullWidth={isMobile}>
              Add Entry
            </Button>
          </Box>

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2 }}>
            <Chip label={`Credit Rs ${formatMoney(ledgerTotals.credit)}`} color="success" variant="outlined" />
            <Chip label={`Debit Rs ${formatMoney(ledgerTotals.debit)}`} color="error" variant="outlined" />
            <Chip label={`Net Rs ${formatMoney(ledgerTotals.net)}`} color={ledgerTotals.net >= 0 ? 'info' : 'warning'} variant="outlined" />
            <Chip label={`${filteredEntries.length} Entries`} variant="outlined" />
          </Box>
        </Paper>

        {dailyLedger.length > 0 ? (
          <Box sx={{ display: 'grid', gap: 1.5 }}>
            {dailyLedger.map((day) => {
              const parsed = parseISO(day.date);
              const label = isValid(parsed) ? format(parsed, 'dd MMM yyyy') : day.date;
              const dayNet = day.credit - day.debit;

              return (
                <Paper key={day.date} elevation={0} sx={{ p: 2, borderRadius: 3, border: `1px solid ${alpha(theme.palette.divider, 0.8)}` }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{label}</Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Chip size="small" label={`Credit Rs ${formatMoney(day.credit)}`} color="success" variant="outlined" />
                      <Chip size="small" label={`Debit Rs ${formatMoney(day.debit)}`} color="error" variant="outlined" />
                      <Chip size="small" label={`Net Rs ${formatMoney(dayNet)}`} color={dayNet >= 0 ? 'info' : 'warning'} variant="outlined" />
                    </Box>
                  </Box>

                  <Divider sx={{ mb: 1.5 }} />

                  <Box sx={{ display: 'grid', gap: 1.2 }}>
                    {day.entries.map((entry) => {
                      const account = accountMap.get(entry.accountId);
                      return (
                        <Box
                          key={entry.id}
                          sx={{
                            p: 1.2,
                            borderRadius: 1.5,
                            border: `1px solid ${alpha(theme.palette.divider, 0.7)}`,
                            display: 'flex',
                            justifyContent: 'space-between',
                            gap: 1.2,
                            alignItems: 'flex-start',
                          }}
                        >
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                              {entry.entryType === 'credit' ? 'Credit' : 'Debit'} Rs {formatMoney(entry.amount)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {account?.accountName || 'Deleted Account'} | {entry.category} | {entry.paymentMode}
                            </Typography>
                            {(entry.referenceNo || entry.notes) && (
                              <Typography variant="caption" color="text.secondary">
                                {[entry.referenceNo ? `Ref: ${entry.referenceNo}` : '', entry.notes].filter(Boolean).join(' | ')}
                              </Typography>
                            )}
                          </Box>
                          <Tooltip title="Delete entry">
                            <IconButton size="small" color="error" onClick={() => handleDeleteEntry(entry.id)}>
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      );
                    })}
                  </Box>
                </Paper>
              );
            })}
          </Box>
        ) : (
          <Paper
            elevation={0}
            sx={{
              p: 4,
              textAlign: 'center',
              borderRadius: 3,
              border: `1px dashed ${alpha(theme.palette.divider, 0.9)}`,
            }}
          >
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No ledger entries found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Add credit or debit entries to see date-wise totals.
            </Typography>
            <Button variant="contained" color="success" startIcon={<Add />} sx={{ mt: 2 }} onClick={() => openEntryDialog()}>
              Add Entry
            </Button>
          </Paper>
        )}

        <Dialog open={accountDialogOpen} onClose={resetAccountForm} fullWidth maxWidth="md" fullScreen={isMobile}>
          <DialogTitle sx={{ bgcolor: theme.palette.primary.main, color: 'common.white' }}>
            {editAccountId ? 'Edit Account' : 'Add Account'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2, display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 2 }}>
              <TextField label="Account Name" value={accountForm.accountName} onChange={handleAccountTextChange('accountName')} size="small" required fullWidth />
              <FormControl size="small" fullWidth>
                <InputLabel>Account Type</InputLabel>
                <Select label="Account Type" value={accountForm.accountType} onChange={(event) => setAccountForm((prev) => ({ ...prev, accountType: event.target.value as AccountType }))}>
                  {ACCOUNT_TYPES.map((type) => (
                    <MenuItem key={type} value={type}>{type}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField label="Contact Person" value={accountForm.contactPerson} onChange={handleAccountTextChange('contactPerson')} size="small" fullWidth />
              <FormControl size="small" fullWidth>
                <InputLabel>Status</InputLabel>
                <Select label="Status" value={accountForm.status} onChange={(event) => setAccountForm((prev) => ({ ...prev, status: event.target.value as AccountStatus }))}>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>

              <TextField label="Phone" value={accountForm.phone} onChange={handleAccountTextChange('phone')} size="small" fullWidth />
              <TextField label="Email" value={accountForm.email} onChange={handleAccountTextChange('email')} type="email" size="small" fullWidth />
              <TextField label="Opening Balance" value={accountForm.openingBalance} onChange={handleOpeningBalanceChange} size="small" fullWidth />
              <TextField label="Notes" value={accountForm.notes} onChange={handleAccountTextChange('notes')} size="small" fullWidth multiline minRows={3} sx={{ gridColumn: isMobile ? '1' : '1 / span 2' }} />
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={resetAccountForm} variant="outlined">Cancel</Button>
            <Button onClick={handleSaveAccount} variant="contained" startIcon={editAccountId ? <Edit /> : <Add />}>
              {editAccountId ? 'Update Account' : 'Save Account'}
            </Button>
          </DialogActions>
        </Dialog>
        {/* comment */}
        <Dialog open={entryDialogOpen} onClose={resetEntryForm} fullWidth maxWidth="sm" fullScreen={isMobile}>
          <DialogTitle sx={{ bgcolor: theme.palette.success.main, color: 'common.white' }}>
            Add Credit or Debit Entry
          </DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2, display: 'grid', gap: 2 }}>
              <FormControl size="small" fullWidth>
                <InputLabel>Account</InputLabel>
                <Select label="Account" value={entryForm.accountId} onChange={(event) => setEntryForm((prev) => ({ ...prev, accountId: event.target.value }))}>
                  {accounts.map((account) => (
                    <MenuItem key={account.id} value={account.id}>{account.accountName}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Box sx={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 2 }}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Entry Type</InputLabel>
                  <Select label="Entry Type" value={entryForm.entryType} onChange={(event) => setEntryForm((prev) => ({ ...prev, entryType: event.target.value as EntryType }))}>
                    <MenuItem value="credit">Credit</MenuItem>
                    <MenuItem value="debit">Debit</MenuItem>
                  </Select>
                </FormControl>
                <TextField label="Amount" size="small" value={entryForm.amount} onChange={handleEntryAmountChange} inputProps={{ min: 0, step: '0.01' }} />
              </Box>

              <DatePicker
                label="Entry Date"
                value={entryForm.entryDate ? parseISO(entryForm.entryDate) : null}
                onChange={(value) => setEntryForm((prev) => ({ ...prev, entryDate: value ? toStorageDate(value) : toStorageDate(new Date()) }))}
                format="dd-MM-yyyy"
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />

              <Box sx={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 2 }}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Category</InputLabel>
                  <Select label="Category" value={entryForm.category} onChange={(event) => setEntryForm((prev) => ({ ...prev, category: event.target.value as EntryCategory }))}>
                    {ENTRY_CATEGORIES.map((category) => (
                      <MenuItem key={category} value={category}>{category}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" fullWidth>
                  <InputLabel>Payment Mode</InputLabel>
                  <Select label="Payment Mode" value={entryForm.paymentMode} onChange={(event) => setEntryForm((prev) => ({ ...prev, paymentMode: event.target.value as PaymentMode }))}>
                    {PAYMENT_MODES.map((mode) => (
                      <MenuItem key={mode} value={mode}>{mode}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <TextField label="Reference Number" value={entryForm.referenceNo} onChange={handleEntryTextChange('referenceNo')} size="small" fullWidth />
              <TextField label="Notes" value={entryForm.notes} onChange={handleEntryTextChange('notes')} size="small" fullWidth multiline minRows={3} />
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={resetEntryForm} variant="outlined">Cancel</Button>
            <Button onClick={handleSaveEntry} variant="contained" color="success">
              Save Entry
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            severity={snackbar.severity}
            onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
            variant="filled"
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </LocalizationProvider>
  );
};

export default AccountsPage;
